import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Badge, Button, Card, Col, Collapse, Container, Input, Row, Spinner, Table } from 'reactstrap';
import AdminLayout from '../../Components/Admin/AdminLayout';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';

const InvitationMonitor = () => {
  const {
    t
  } = useTranslation();
  const [invitations, setInvitations] = useState([]);
  const [groupedInvitations, setGroupedInvitations] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});
  const [selectedInvitations, setSelectedInvitations] = useState({});
  const [selectedGroups, setSelectedGroups] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  const toggleSelectGroup = invitationId => {
    setSelectedGroups(prev => ({ ...prev,
      [invitationId]: !prev[invitationId]
    }));
  };

  const toggleSelectAllGroups = checked => {
    setSelectedGroups(() => {
      const next = {};

      if (checked) {
        Object.keys(groupedInvitations).forEach(id => {
          next[id] = true;
        });
      }

      return next;
    });
  };

  const deleteSelectedGroups = async () => {
    const toDelete = Object.keys(selectedGroups).filter(id => selectedGroups[id]);
    if (toDelete.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete the ${toDelete.length} selected invitation group(s) and all related data?`)) {
      return;
    }

    try {
      setUpdating(prev => {
        const next = { ...prev
        };
        toDelete.forEach(id => {
          next[id] = true;
        });
        return next;
      });
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const baseUrl = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;

      for (const invitationId of toDelete) {
        const response = await fetch(`${baseUrl}/admin/invitations/${invitationId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error(`Failed to delete invitation ${invitationId}`);
        }
      }

      setInvitations(prev => prev.filter(inv => !selectedGroups[inv.invitationId]));
      setSelectedGroups({});
    } catch (error) {
      console.error('Error deleting selected groups:', error);
      alert(`Failed to delete some invitations: ${error.message}`);
    } finally {
      setUpdating(prev => {
        const next = { ...prev
        };
        toDelete.forEach(id => {
          next[id] = false;
        });
        return next;
      });
    }
  };

  const toggleSelectInvitation = id => {
    setSelectedInvitations(prev => ({ ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleSelectAllInGroup = (groupInvitations, checked) => {
    setSelectedInvitations(prev => {
      const next = { ...prev
      };
      groupInvitations.forEach(inv => {
        next[inv.invitationId] = checked;
      });
      return next;
    });
  };

  const deleteSelectedInvitations = async groupInvitations => {
    const toDelete = groupInvitations.filter(inv => selectedInvitations[inv.invitationId]);
    if (toDelete.length === 0) return;
    if (!window.confirm(`Delete ${toDelete.length} selected invitation(s)? This cannot be undone.`)) return; // Perform deletions

    for (const inv of toDelete) {
      await deleteInvitation(inv.invitationId);
    } // Clear selection for these


    setSelectedInvitations(prev => {
      const next = { ...prev
      };
      toDelete.forEach(inv => {
        delete next[inv.invitationId];
      });
      return next;
    });
  };

  useEffect(() => {
    fetchInvitations();
  }, []);
  const groupInvitationsByInvitationId = useCallback(() => {
    const grouped = {};
    invitations.forEach(invitation => {
      const invitationId = invitation.invitationId || 'no-invitation-id';

      if (!grouped[invitationId]) {
        grouped[invitationId] = {
          invitationId: invitationId,
          inviteCode: invitation.inviteCode,
          invitations: [],
          status: invitation.status || 'unknown',
          createdAt: invitation.createdAt,
          updatedAt: invitation.updatedAt,
          senderEmail: invitation.senderEmail || invitation.senderUsername,
          senderName: invitation.senderName
        };
      }

      grouped[invitationId].invitations.push(invitation); // Update group status based on invitation statuses

      if (invitation.status === 'connected' || invitation.status === 'accepted') {
        grouped[invitationId].status = 'connected';
      } else if (invitation.status === 'pending' && grouped[invitationId].status !== 'connected') {
        grouped[invitationId].status = 'pending';
      } else if (invitation.status === 'declined' && grouped[invitationId].status === 'unknown') {
        grouped[invitationId].status = 'declined';
      } // Update timestamps


      if (new Date(invitation.createdAt) < new Date(grouped[invitationId].createdAt)) {
        grouped[invitationId].createdAt = invitation.createdAt;
      }

      if (new Date(invitation.updatedAt) > new Date(grouped[invitationId].updatedAt)) {
        grouped[invitationId].updatedAt = invitation.updatedAt;
      }
    });
    setGroupedInvitations(grouped);
  }, [invitations]);
  useEffect(() => {
    groupInvitationsByInvitationId();
  }, [invitations, groupInvitationsByInvitationId]);

  const toggleGroup = invitationId => {
    setExpandedGroups(prev => ({ ...prev,
      [invitationId]: !prev[invitationId]
    }));
  };

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('HMI_AUTH_TOKEN'); // Handle both cases: API_BASE with and without /api suffix

      const baseUrl = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;
      const response = await fetch(`${baseUrl}/admin/invitations/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API Response:', data);

      if (data.success) {
        // Handle both old and new API response formats
        const invitationsData = data.data.invitations || data.data || [];
        console.log('Invitations data:', invitationsData);
        setInvitations(Array.isArray(invitationsData) ? invitationsData : []);
      } else {
        throw new Error(data.error?.message || 'Failed to fetch invitations');
      }
    } catch (err) {
      console.error('Error fetching invitations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (invitationId, newStatus) => {
    try {
      setUpdating(prev => ({ ...prev,
        [invitationId]: true
      }));
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const baseUrl = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;
      const response = await fetch(`${baseUrl}/admin/invitations/${invitationId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      } // Update local state


      setInvitations(prev => prev.map(inv => inv.invitationId === invitationId ? { ...inv,
        status: newStatus,
        updatedAt: new Date().toISOString()
      } : inv));
    } catch (error) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setUpdating(prev => ({ ...prev,
        [invitationId]: false
      }));
    }
  };

  const deleteInvitation = async invitationId => {
    if (!window.confirm('Are you sure you want to delete this invitation and all related data?')) {
      return;
    }

    try {
      setUpdating(prev => ({ ...prev,
        [invitationId]: true
      }));
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const baseUrl = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;
      const response = await fetch(`${baseUrl}/admin/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      } // Remove from local state


      setInvitations(prev => prev.filter(inv => inv.invitationId !== invitationId));
    } catch (error) {
      console.error('Error deleting invitation:', error);
      alert(`Failed to delete invitation: ${error.message}`);
    } finally {
      setUpdating(prev => ({ ...prev,
        [invitationId]: false
      }));
    }
  };

  const formatRecipient = invitation => {
    if (invitation.recipientName) {
      return `${invitation.recipientName} (${invitation.recipientEmail})`;
    } else if (invitation.toEmail) {
      return invitation.toEmail;
    } else if (invitation.toPhone) {
      return invitation.toPhone;
    }

    return 'Unknown';
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const getStatusBadge = status => {
    const badges = {
      'pending': 'warning',
      'connected': 'success',
      'declined': 'danger',
      'expired': 'secondary'
    };
    return badges[status] || 'secondary';
  };

  if (loading) {
    return <AdminLayout>
                <div style={{
        padding: 20,
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
                    <div className="text-center">
                        <Spinner color="primary" />
                        <p className="mt-2">{t("Loading invitations...")}</p>
                    </div>
                </div>
            </AdminLayout>;
  }

  if (error) {
    return <AdminLayout>
                <div style={{
        padding: 20,
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
                    <Alert color="danger">
                        <h4>{t("Error")}</h4>
                        <p>{error}</p>
                        <Button color="primary" onClick={fetchInvitations}>{t("Retry")}</Button>
                    </Alert>
                </div>
            </AdminLayout>;
  }

  return <AdminLayout>
            <div style={{
      padding: 20,
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
                <h4 className="mb-4" style={{
        marginBottom: '15px',
        fontSize: '1.5rem',
        textTransform: 'none',
        letterSpacing: 'normal',
        fontWeight: 600,
        color: 'white'
      }}>{t("Invitation Management")}</h4>
                
                <div style={{
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center'
      }}>
                    <label style={{
          marginRight: '10px',
          fontWeight: 'bold',
          marginBottom: 0
        }}>{t("Sort/Filter:")}</label>
                    <select className="form-control" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{
          width: '200px'
        }}>
                        <option value="all">{t("All Statuses")}</option>
                        <option value="pending">{t("Pending")}</option>
                        <option value="connected">{t("Connected")}</option>
                        <option value="declined">{t("Declined")}</option>
                        <option value="expired">{t("Expired")}</option>
                    </select>
                </div>

                <Container className="mt-4">
                    <Row>
                        <Col>
                             <Card>
                                <div className="card-header bg-primary text-white d-flex align-items-center justify-content-between flex-wrap">
                                    <div className="d-flex align-items-center">
                                        <h4 className="mb-0 mr-3"><span role="img" aria-label="clipboard">📋</span>{t("Invitation Management")}</h4>
                                        <div className="d-flex align-items-center ml-4" style={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    padding: '4px 10px',
                    borderRadius: '6px'
                  }}>
                                            <input type="checkbox" id="checkAllGroups" checked={Object.keys(groupedInvitations).length > 0 && Object.keys(groupedInvitations).every(id => selectedGroups[id])} onChange={e => toggleSelectAllGroups(e.target.checked)} style={{
                      cursor: 'pointer',
                      transform: 'scale(1.2)'
                    }} />
                                            <label htmlFor="checkAllGroups" className="mb-0 ml-2 text-white font-weight-bold" style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: '0.9rem'
                    }}>{t("Select All Groups")}</label>
                                        </div>
                                    </div>
                                    {Object.values(selectedGroups).filter(Boolean).length > 0 && <Button color="danger" size="sm" onClick={deleteSelectedGroups} style={{
                  fontWeight: '600'
                }}>{t("Delete Selected Groups (")}{Object.values(selectedGroups).filter(Boolean).length})
                                        </Button>}
                                </div>
                                <div className="card-body">
                                                                    {Object.keys(groupedInvitations).length === 0 ? <p className="text-muted text-center">{t("No invitations found.")}</p> : <div>
                                                                            {Object.entries(groupedInvitations).filter(([id, group]) => {
                    if (filterStatus === 'all') return true;
                    return group && group.status === filterStatus;
                  }).map(([invitationId, group]) => <Card key={invitationId} className="mb-3">
                                                                                    <div className="card-header cursor-pointer" onClick={() => toggleGroup(invitationId)} style={{
                      cursor: 'pointer'
                    }}>
                                                                                        <Row className="align-items-center">
                                                                                            <Col xs="auto" className="pr-0">
                                                                                                <input type="checkbox" checked={!!selectedGroups[invitationId]} onChange={e => {
                            e.stopPropagation();
                            toggleSelectGroup(invitationId);
                          }} style={{
                            cursor: 'pointer',
                            transform: 'scale(1.2)'
                          }} />
                                                                                            </Col>
                                                                                            <Col md="4">
                                                                                                <div>
                                                                                                    <strong>
                                                                                                        <Badge color="primary" className="mr-2">{t("Invitation ID:")}{invitationId.substring(0, 20)}...
                                                                                                        </Badge>
                                                                                                        {group.inviteCode && <Badge color="info" className="mr-2">{t("Invite Code:")}{group.inviteCode}
                                                                                                            </Badge>}
                                                                                                    </strong>
                                                                                                    <br />
                                                                                                    <small className="text-muted">
                                                                                                        {group.invitations.length}{t("invitation")}{group.invitations.length !== 1 ? 's' : ''}{t("\u2022 From:")}{group.senderName || group.senderEmail}
                                                                                                    </small>
                                                                                                </div>
                                                                                            </Col>
                                                                                            <Col md="3">
                                                                                                <Badge color={getStatusBadge(group.status)}>
                                                                                                    {group.status}
                                                                                                </Badge>
                                                                                            </Col>
                                                                                            <Col md="3">
                                                                                                <small className="text-muted">{t("Created:")}{formatDate(group.createdAt)}
                                                                                                    <br />{t("Updated:")}{formatDate(group.updatedAt)}
                                                                                                </small>
                                                                                            </Col>
                                                                                            <Col md="2" className="text-right d-flex align-items-center justify-content-end">
                                                                                                <Button color="danger" size="sm" className="mr-3" onClick={e => {
                            e.stopPropagation();
                            deleteInvitation(invitationId);
                          }} disabled={group.invitations.some(inv => updating[inv.invitationId])}>
                                                                                                    {group.invitations.some(inv => updating[inv.invitationId]) ? <Spinner size="sm" /> : 'Delete'}
                                                                                                </Button>
                                                                                                <Badge color="secondary" style={{
                            minWidth: '24px'
                          }}>
                                                                                                    {expandedGroups[invitationId] ? '▼' : '▶'}
                                                                                                </Badge>
                                                                                            </Col>
                                                                                        </Row>
                                                                                    </div>

                                                                           <Collapse isOpen={expandedGroups[invitationId]}>
                                                        <div className="card-body">
                                                            {(() => {
                          const selectedInGroup = group.invitations.filter(inv => selectedInvitations[inv.invitationId]);
                          const isAllSelectedInGroup = group.invitations.length > 0 && selectedInGroup.length === group.invitations.length;
                          const hasSelectedInGroup = selectedInGroup.length > 0;
                          return <>
                                                                        {hasSelectedInGroup && <div className="mb-2 p-2 d-flex align-items-center justify-content-between border rounded">
                                                                                <span className="text-danger font-weight-bold" style={{
                                fontSize: '0.9rem'
                              }}>
                                                                                    {selectedInGroup.length}{t("invitation(s) selected")}</span>
                                                                                <Button color="danger" size="sm" onClick={() => deleteSelectedInvitations(group.invitations)} style={{
                                fontSize: '0.85rem',
                                fontWeight: '600'
                              }}>{t("Delete Selected")}</Button>
                                                                            </div>}
                                                                        <Table responsive striped size="sm">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th style={{
                                    width: '40px',
                                    textAlign: 'center'
                                  }}>
                                                                                        <input type="checkbox" checked={isAllSelectedInGroup} onChange={e => toggleSelectAllInGroup(group.invitations, e.target.checked)} style={{
                                      cursor: 'pointer'
                                    }} />
                                                                                    </th>
                                                                                    <th>{t("Sender")}</th>
                                                                                    <th>{t("Recipient")}</th>
                                                                                    <th>{t("Invitation Cards")}</th>
                                                                                    <th>{t("Response Cards")}</th>
                                                                                    <th>{t("Status")}</th>
                                                                                    <th>{t("Created")}</th>
                                                                                    <th>{t("Updated")}</th>
                                                                                    <th>{t("Actions")}</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {group.invitations.map((invitation, index) => <tr key={`${invitation.invitationId}_${invitation.recipientId || invitation.recipientEmail || invitation.toEmail || invitation.toPhone || index}`}>
                                                                                        <td style={{
                                    textAlign: 'center',
                                    verticalAlign: 'middle'
                                  }}>
                                                                                            <input type="checkbox" checked={!!selectedInvitations[invitation.invitationId]} onChange={() => toggleSelectInvitation(invitation.invitationId)} style={{
                                      cursor: 'pointer'
                                    }} />
                                                                                        </td>
                                                                                        <td>
                                                                                            <strong>{invitation.senderName || invitation.senderUsername || invitation.senderEmail}</strong>
                                                                                            <br />
                                                                                            <small className="text-muted">{invitation.senderEmail}</small>
                                                                                        </td>
                                                                                        <td>
                                                                                            <strong>{formatRecipient(invitation)}</strong>
                                                                                            {invitation.recipientId && <>
                                                                                                    <br />
                                                                                                    <small className="text-muted">{t("Registered User")}</small>
                                                                                                </>}
                                                                                        </td>
                                                                                        <td>
                                                                                            <small>
                                                                                                {(() => {
                                        try {
                                          const cards = typeof invitation.connectionCards === 'string' ? JSON.parse(invitation.connectionCards) : invitation.connectionCards || [];
                                          if (!Array.isArray(cards) || cards.length === 0) return '[]';
                                          return `[${cards.map(c => typeof c === 'string' ? c : c && c.name ? c.name : c && c.id ? c.id : 'Unknown').join(', ')}]`;
                                        } catch (e) {
                                          return '[]';
                                        }
                                      })()}
                                                                                            </small>
                                                                                        </td>
                                                                                        <td>
                                                                                            <small>
                                                                                                {(() => {
                                        try {
                                          const cards = typeof invitation.responseCards === 'string' ? JSON.parse(invitation.responseCards) : invitation.responseCards || [];
                                          if (!Array.isArray(cards) || cards.length === 0) return '[]';
                                          return `[${cards.map(c => typeof c === 'string' ? c : c && c.name ? c.name : c && c.id ? c.id : 'Unknown').join(', ')}]`;
                                        } catch (e) {
                                          return '[]';
                                        }
                                      })()}
                                                                                            </small>
                                                                                        </td>
                                                                                        <td>
                                                                                            <Input type="select" value={invitation.status} onChange={e => updateStatus(invitation.invitationId, e.target.value)} disabled={updating[invitation.invitationId]} bsSize="sm">
                                                                                                <option value="pending">{t("Pending")}</option>
                                                                                                <option value="connected">{t("Connected")}</option>
                                                                                                <option value="declined">{t("Declined")}</option>
                                                                                                <option value="expired">{t("Expired")}</option>
                                                                                            </Input>
                                                                                        </td>
                                                                                        <td>
                                                                                            <small>{formatDate(invitation.createdAt)}</small>
                                                                                        </td>
                                                                                        <td>
                                                                                            <small>{formatDate(invitation.updatedAt)}</small>
                                                                                        </td>
                                                                                        <td>
                                                                                            <Button color="danger" size="sm" onClick={() => deleteInvitation(invitation.invitationId)} disabled={updating[invitation.invitationId]}>
                                                                                                {updating[invitation.invitationId] ? <Spinner size="sm" /> : '🗑️ Delete'}
                                                                                            </Button>
                                                                                        </td>
                                                                                    </tr>)}
                                                                            </tbody>
                                                                        </Table>
                                                                    </>;
                        })()}
                                                        </div>
                                                    </Collapse>
                                                </Card>)}
                                        </div>}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </AdminLayout>;
};

export default InvitationMonitor;